// DataStorm - Python Data Science
// Histoire : Un deluge de donnees menace l'entreprise. Le Data Engineer doit maitriser le chaos.

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
  TaskContent,
} from './storyLoader';

export interface DataStormScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  coverImage: ReturnType<typeof require>;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const dataStormCharacters: Character[] = [
  { id: 'aisha',   name: 'Aisha',   role: 'Data Engineer Lead', color: '#065F46', avatar: 'aisha'   },
  { id: 'marcus',  name: 'Marcus',  role: 'Data Scientist',     color: '#1E3A5F', avatar: 'marcus'  },
  { id: 'darius',  name: 'Darius',  role: 'CTO Analytics',      color: '#7C2D12', avatar: 'darius'  },
  { id: 'jun',     name: 'Jun',     role: 'Data Analyst',       color: '#4338CA', avatar: 'jun'     },
  { id: 'soomin',  name: 'Soo-Min', role: 'BI Engineer',        color: '#701A75', avatar: 'soomin'  },
];

// ── Chapitre I : Le Deluge ─────────────────────────────────────────────────────

const scene1: SequenceItem = {
  type: 'story',
  id: 'ds-scene-1',
  title: 'Scene 1 : Alerte systeme',
  duration: 4,
  content: {
    narrativeText:
      "03h17. Le datacenter clignote rouge.\n\n" +
      "Soo-Min est reveillée par son téléphone. Alerte critique sur le pipeline.\n\n" +
      "**Soo-Min :** \"Aisha, le pipeline est sature. 42 millions de transactions dans la queue. La memoire est a 98%.\"\n\n" +
      "**Aisha :** \"Quoi ? Ca aurait du declencher l'auto-scaling. Qu'est-ce qui se passe ?\"\n\n" +
      "**Soo-Min :** \"Les transactions arrivent 10x plus vite que prevu. E-commerce mondial — le black friday asiatique.\"\n\n" +
      "**Aisha :** \"Appelle Darius. Et reveille Marcus — on a besoin du modele de detection d'anomalies.\"\n\n" +
      "**Darius (par tél) :** \"J'arrive dans 20 minutes. Donnez-moi l'etat exact.\"\n\n" +
      "**Aisha :** \"Pipeline batch sature, Kafka en backlog de 42M messages, Redis OOM depuis 8 minutes.\"\n\n" +
      "**Darius :** \"Options : on scale horizontalement ou on switch en streaming. On a 30 minutes avant l'effondrement.\"\n\n" +
      "**Aisha :** \"Streaming. Spark Structured Streaming peut absorber ca en temps reel. Marcus, tu peux migrer le pipeline ?\"\n\n" +
      "**Marcus :** \"Je peux. Mais on va perdre les transformations complexes. On simplifie, on livre, on affine apres.\"\n\n" +
      "**Aisha :** \"Accord. Le deluge commence. On nage ou on coule.\"",
    characters: ['aisha', 'marcus', 'darius', 'soomin'],
  } as StoryContent,
};

const card1: SequenceItem = {
  type: 'card',
  id: 'ds-card-1',
  title: 'Concept : Architecture Data Pipeline',
  duration: 5,
  content: {
    title: 'Architecture Data Pipeline : des donnees brutes a la valeur',
    body:
      "## Architecture Data Pipeline\n\n" +
      "Un pipeline de donnees transforme les donnees brutes en informations exploitables.\n\n" +
      "### Les 4 couches\n\n" +
      "#### 1. Ingestion\n" +
      "- **Batch** : traitement periodique (ex: toutes les heures) — simpler mais latence elevee\n" +
      "- **Streaming** : traitement en temps reel (ex: Kafka + Spark Streaming) — complexe mais immediat\n" +
      "- Outils Python : pandas (batch), Kafka-Python (streaming)\n\n" +
      "#### 2. Transformation\n" +
      "- Nettoyage : suppression des doublons, gestion des valeurs manquantes\n" +
      "- Enrichissement : jointures avec des tables de reference\n" +
      "- Agregation : calculs statistiques, groupby\n" +
      "- Outils : pandas, dbt, Apache Spark\n\n" +
      "#### 3. Stockage\n" +
      "- Data Lake : donnees brutes (S3, ADLS Gen2)\n" +
      "- Data Warehouse : donnees structurees (Snowflake, BigQuery, Synapse)\n" +
      "- Feature Store : features ML precalculees\n\n" +
      "#### 4. Serving\n" +
      "- Dashboards BI (Power BI, Tableau)\n" +
      "- APIs de donnees (FastAPI + SQLAlchemy)\n" +
      "- Modeles ML en production\n\n" +
      "### Batch vs Streaming\n" +
      "| Critere | Batch | Streaming |\n" +
      "|---|---|---|\n" +
      "| Latence | Minutes/heures | Millisecondes/secondes |\n" +
      "| Complexite | Faible | Elevee |\n" +
      "| Cas d'usage | Reporting quotidien | Fraude temps reel |",
  } as CardContent,
};

const chat1: SequenceItem = {
  type: 'chat',
  id: 'ds-chat-1',
  title: 'Discussion CTO : Batch vs Streaming',
  duration: 6,
  content: {
    tutorCharacterId: 'darius',
    messages: [
      {
        id: 'ds-chat-1-q1',
        sender: 'character',
        characterId: 'darius',
        text: "Aisha, face a 42M transactions en attente, quelle est la PREMIERE action a prendre ?",
        type: 'single-choice',
        options: [
          "Migrer immediatement vers Spark Streaming",
          "Scaler horizontalement les workers Kafka",
          "Identifier le goulot d'etranglement avec les metriques systeme",
          "Vider la queue et recommencer",
        ],
        correctAnswer: "Identifier le goulot d'etranglement avec les metriques systeme",
        explanation: "Avant de prendre une decision, il faut diagnostiquer : CPU, memoire, I/O, backlog Kafka. Sans diagnostic, on risque de resoudre le mauvais probleme. 'Measure, don't guess.'",
      },
      {
        id: 'ds-chat-1-q2',
        sender: 'character',
        characterId: 'darius',
        text: "Quand choisit-on le streaming plutot que le batch pour un pipeline ?",
        type: 'multiple-choice',
        options: [
          "Quand la latence maximale tolerable est inferieure a 1 minute",
          "Quand les donnees arrivent de facon continue (ex: logs, transactions)",
          "Quand on a besoin de jointures complexes multi-tables",
          "Quand la detection de fraude en temps reel est requise",
        ],
        correctAnswer: [
          "Quand la latence maximale tolerable est inferieure a 1 minute",
          "Quand les donnees arrivent de facon continue (ex: logs, transactions)",
          "Quand la detection de fraude en temps reel est requise",
        ],
        explanation: "Le streaming est ideal pour les donnees continues avec une exigence de latence faible. Les jointures complexes sont plus difficiles en streaming car il faut gerer les fenetres temporelles.",
      },
      {
        id: 'ds-chat-1-q3',
        sender: 'character',
        characterId: 'darius',
        text: "Dans pandas, quelle methode utilise-t-on pour traiter de grands CSV par chunks pour eviter l'OOM ?",
        type: 'single-choice',
        options: [
          "pd.read_csv(file, nrows=1000)",
          "pd.read_csv(file, chunksize=10000)",
          "pd.read_csv(file, memory_map=True)",
          "pd.load_csv(file, streaming=True)",
        ],
        correctAnswer: "pd.read_csv(file, chunksize=10000)",
        explanation: "pd.read_csv(chunksize=N) retourne un iterator. Chaque iteration charge N lignes en memoire, ce qui evite de charger tout le fichier d'un coup. Ideal pour les fichiers > 1GB.",
      },
    ],
  } as ChatContent,
};

const task1: SequenceItem = {
  type: 'task',
  id: 'ds-task-1',
  title: 'Creer un pipeline pandas de nettoyage de donnees',
  duration: 30,
  content: {
    platform: 'Python / Jupyter Notebook',
    objective: "Construire un pipeline de nettoyage et transformation des 42M transactions",
    steps: [
      {
        stepNumber: 1,
        action: "Importer les bibliotheques",
        detail: "import pandas as pd; import numpy as np; from datetime import datetime",
      },
      {
        stepNumber: 2,
        action: "Lire le CSV par chunks",
        detail: "chunks = pd.read_csv('transactions.csv', chunksize=100_000); df = pd.concat(chunks)",
      },
      {
        stepNumber: 3,
        action: "Supprimer les doublons et valeurs manquantes",
        detail: "df = df.drop_duplicates(subset=['transaction_id']); df = df.dropna(subset=['amount', 'user_id'])",
      },
      {
        stepNumber: 4,
        action: "Caster les types",
        detail: "df['amount'] = pd.to_numeric(df['amount'], errors='coerce'); df['created_at'] = pd.to_datetime(df['created_at'])",
      },
      {
        stepNumber: 5,
        action: "Exporter le resultat",
        detail: "df.to_csv('transactions_clean.csv', index=False); print(f'Lignes nettoyees: {len(df)}')",
      },
    ],
    validation: "Pipeline execute sans erreur. Rapport de qualite : nb de lignes avant/apres, % de doublons supprimes.",
  } as TaskContent,
};

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'ds-quiz-1',
  title: 'Quiz : pandas et Data Engineering',
  duration: 5,
  content: {
    questions: [
      {
        id: 'ds-q1-1',
        question: "Quelle est la difference entre merge() et join() dans pandas ?",
        type: 'single-choice',
        options: [
          "Aucune difference, ce sont des alias",
          "merge() est plus flexible (jointure sur colonnes quelconques), join() joint par l'index",
          "join() supporte les outer joins, pas merge()",
          "merge() est plus rapide sur les grands DataFrames",
        ],
        correctAnswer: "merge() est plus flexible (jointure sur colonnes quelconques), join() joint par l'index",
        explanation: "pd.merge(df1, df2, on='col') est l'equivalent d'un SQL JOIN sur une colonne. df1.join(df2) joint par l'index. merge() est generalement prefere pour sa flexibilite.",
      },
      {
        id: 'ds-q1-2',
        question: "Dans pandas, quel est le moyen recommande pour grouper et aggreger en meme temps ?",
        type: 'single-choice',
        options: [
          "df.group() puis df.agg()",
          "df.groupby('col').agg({'val': 'sum'})",
          "df.pivot_table(index='col', values='val')",
          "df.apply(lambda x: x.sum())",
        ],
        correctAnswer: "df.groupby('col').agg({'val': 'sum'})",
        explanation: "groupby().agg() permet d'appliquer plusieurs fonctions d'agregation sur plusieurs colonnes en une seule operation. C'est la methode la plus lisible et efficace.",
      },
      {
        id: 'ds-q1-3',
        question: "Quelle strategie de fillna() est la plus adaptee pour des series temporelles ?",
        type: 'single-choice',
        options: [
          "fillna(0) — remplacer par zero",
          "fillna(df.mean()) — remplacer par la moyenne",
          "fillna(method='ffill') — propager la valeur precedente",
          "dropna() — supprimer les lignes",
        ],
        correctAnswer: "fillna(method='ffill') — propager la valeur precedente",
        explanation: "Pour les series temporelles, la methode forward-fill (ffill) est la plus naturelle : on suppose que la valeur ne change pas jusqu'a la prochaine observation. fillna(0) peut introduire des biais artificiels.",
      },
    ],
  } as QuizContent,
};

const scene2: SequenceItem = {
  type: 'story',
  id: 'ds-scene-2',
  title: "Scene 2 : Decouverte d'anomalies",
  duration: 4,
  content: {
    narrativeText:
      "06h30. Le pipeline a absorbe le deluge. Mais Marcus decouvre quelque chose d'inquietant.\n\n" +
      "**Marcus :** \"Aisha, regarde ces transactions. Des clusters de montants quasi-identiques a quelques centimes pres.\"\n\n" +
      "**Jun :** \"Pattern classique de fraud testing. Des petits montants pour tester si une carte fonctionne avant les gros achats.\"\n\n" +
      "**Marcus :** \"Exactement. Et le modele de scoring n'a pas flaggue ca. Le z-score normal ne capte pas ce type d'anomalie.\"\n\n" +
      "**Aisha :** \"Combien de transactions suspectes ?\"\n\n" +
      "**Marcus :** \"240,000. Soit 0.57% du volume. Mais si c'est de la fraude coordonnee, on parle de potentiellement 2M euros.\"\n\n" +
      "**Darius :** \"On alerte le RSSI tout de suite. Et on ameliore le modele de detection. Marcus, tu peux implementer une detection par isolation forest ?\"\n\n" +
      "**Marcus :** \"Oui. Plus robuste que le z-score pour ce type de fraude. Je code ca maintenant.\"\n\n" +
      "**Aisha :** \"Jun, tu coordonnes avec le legal. Ces donnees doivent etre preservees pour l'enquete.\"",
    characters: ['aisha', 'marcus', 'darius', 'jun'],
  } as StoryContent,
};

const card2: SequenceItem = {
  type: 'card',
  id: 'ds-card-2',
  title: "Concept : Detection d'anomalies statistiques",
  duration: 5,
  content: {
    title: "Detection d'anomalies : Z-score, IQR et Isolation Forest",
    body:
      "## Detection d'anomalies statistiques\n\n" +
      "### 1. Z-score (Ecart-type)\n" +
      "```python\nfrom scipy import stats\nz_scores = np.abs(stats.zscore(df['amount']))\nanomalies = df[z_scores > 3]  # Plus de 3 ecarts-types\n```\n" +
      "**Avantages** : Simple, interpretable. **Limites** : Sensible aux outliers, suppose distribution normale.\n\n" +
      "### 2. IQR (Interquartile Range)\n" +
      "```python\nQ1 = df['amount'].quantile(0.25)\nQ3 = df['amount'].quantile(0.75)\nIQR = Q3 - Q1\nanomalies = df[(df['amount'] < Q1 - 1.5*IQR) | (df['amount'] > Q3 + 1.5*IQR)]\n```\n" +
      "**Avantages** : Robuste, pas de distribution supposee. **Limites** : Univarie uniquement.\n\n" +
      "### 3. Isolation Forest (ML)\n" +
      "```python\nfrom sklearn.ensemble import IsolationForest\nmodel = IsolationForest(contamination=0.01, random_state=42)\ndf['anomaly'] = model.fit_predict(df[['amount', 'frequency', 'velocity']])\nanomalies = df[df['anomaly'] == -1]\n```\n" +
      "**Avantages** : Multivariate, adapte aux donnees non-gaussiennes, detecte les fraudes coordonnees.\n\n" +
      "### Quand utiliser quoi ?\n" +
      "| Methode | Dimensions | Distribution | Cas d'usage |\n" +
      "|---|---|---|---|\n" +
      "| Z-score | 1D | Gaussienne | Monitoring simple |\n" +
      "| IQR | 1D | Quelconque | Outliers univaries |\n" +
      "| Isolation Forest | nD | Quelconque | Fraude, anomalies complexes |",
  } as CardContent,
};

const task2: SequenceItem = {
  type: 'task',
  id: 'ds-task-2',
  title: "Implementer une detection d'anomalies avec numpy/sklearn",
  duration: 35,
  content: {
    platform: 'Python / Jupyter Notebook',
    objective: "Implementer un systeme de detection d'anomalies pour identifier les transactions frauduleuses",
    steps: [
      {
        stepNumber: 1,
        action: "Preparer les features",
        detail: "df['hour'] = df['created_at'].dt.hour; df['velocity'] = df.groupby('user_id')['transaction_id'].transform('count'); df['amount_zscore'] = np.abs(stats.zscore(df['amount']))",
      },
      {
        stepNumber: 2,
        action: "Calculer les z-scores",
        detail: "from scipy import stats; z_scores = np.abs(stats.zscore(df[['amount', 'velocity']])); df['is_outlier_zscore'] = (z_scores > 3).any(axis=1)",
      },
      {
        stepNumber: 3,
        action: "Implementer Isolation Forest",
        detail: "from sklearn.ensemble import IsolationForest; model = IsolationForest(contamination=0.005, random_state=42); df['anomaly_if'] = model.fit_predict(df[['amount', 'velocity', 'hour']])",
      },
      {
        stepNumber: 4,
        action: "Comparer les methodes",
        detail: "print(f'Z-score: {df.is_outlier_zscore.sum()} anomalies'); print(f'Isolation Forest: {(df.anomaly_if==-1).sum()} anomalies')",
      },
      {
        stepNumber: 5,
        action: "Exporter le rapport",
        detail: "suspicious = df[df['anomaly_if'] == -1]; suspicious.to_csv('suspicious_transactions.csv', index=False)",
      },
    ],
    validation: "Rapport d'anomalies genere. Comparaison Z-score vs Isolation Forest documentee.",
  } as TaskContent,
};

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'Le Deluge',
  subtitle: 'Python Data Science — Maitriser le chaos des donnees',
  learningObjectives: [
    "Construire un pipeline pandas de nettoyage de donnees",
    "Comprendre Batch vs Streaming pour les architectures data",
    "Implementer des algorithmes de detection d'anomalies",
    "Appliquer Z-score, IQR et Isolation Forest",
    "Gerer les donnees volumineuses avec chunking",
  ],
  sequence: [scene1, card1, chat1, task1, quiz1, scene2, card2, task2],
};

export const dataStormScenario: DataStormScenario = {
  id: 'data-storm',
  title: 'Data Storm',
  subtitle: 'Maitriser 42 millions de transactions en temps reel',
  certification: 'Python Data Science',
  coverImage: require('../../../assets/images/characters/nina_raoul_laptop.png'),
  characters: dataStormCharacters,
  chapters: [chapter1],
};

export default dataStormScenario;
